/**
 * Messages UI State Slice
 * Handles LOCAL UI state for messages/feedback feature (not API calls)
 * API calls are handled by features/messages/messagesApiSlice.ts
 */

import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";

export interface MessagesUIState {
  // Selected message for detail view
  selectedMessageId: string | null;

  // Search/filter state
  searchQuery: string;
  filterByStatus: "all" | "delivered" | "pending";
  filterByExperience: "all" | "good" | "average" | "poor";

  // UI preferences
  viewMode: "table" | "cards";
  sortBy: "timestamp" | "vehicleNumber" | "experience";
  sortOrder: "asc" | "desc";

  // Pagination state
  currentPage: number;
  itemsPerPage: number;
}

const initialState: MessagesUIState = {
  selectedMessageId: null,
  searchQuery: "",
  filterByStatus: "all",
  filterByExperience: "all",
  viewMode: "table",
  sortBy: "timestamp",
  sortOrder: "desc",
  currentPage: 1,
  itemsPerPage: 10,
};

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    // Selection
    setSelectedMessage: (state, action: PayloadAction<string | null>) => {
      state.selectedMessageId = action.payload;
    },

    // Search and filters
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.currentPage = 1; // Reset to first page on search
    },

    setFilterByStatus: (
      state,
      action: PayloadAction<"all" | "delivered" | "pending">
    ) => {
      state.filterByStatus = action.payload;
      state.currentPage = 1;
    },

    setFilterByExperience: (
      state,
      action: PayloadAction<"all" | "good" | "average" | "poor">
    ) => {
      state.filterByExperience = action.payload;
      state.currentPage = 1;
    },

    // View preferences
    setViewMode: (state, action: PayloadAction<"table" | "cards">) => {
      state.viewMode = action.payload;
    },

    setSortBy: (
      state,
      action: PayloadAction<"timestamp" | "vehicleNumber" | "experience">
    ) => {
      state.sortBy = action.payload;
    },

    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.sortOrder = action.payload;
    },

    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === "asc" ? "desc" : "asc";
    },

    // Pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1; // Reset to first page
    },

    // Reset all filters
    resetFilters: (state) => {
      state.searchQuery = "";
      state.filterByStatus = "all";
      state.filterByExperience = "all";
      state.currentPage = 1;
    },
  },
});

export const {
  setSelectedMessage,
  setSearchQuery,
  setFilterByStatus,
  setFilterByExperience,
  setViewMode,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  setCurrentPage,
  setItemsPerPage,
  resetFilters,
} = messagesSlice.actions;

/**
 * Memoized selectors for messages UI state
 * Use createSelector for optimal performance
 */

// Base selector
const selectMessagesState = (state: { messages: MessagesUIState }) =>
  state.messages;

// Memoized selectors
export const selectMessagesUI = createSelector(
  [selectMessagesState],
  (messages) => messages
);

export const selectSelectedMessageId = createSelector(
  [selectMessagesState],
  (messages) => messages.selectedMessageId
);

export const selectSearchQuery = createSelector(
  [selectMessagesState],
  (messages) => messages.searchQuery
);

export const selectMessagesFilters = createSelector(
  [selectMessagesState],
  (messages) => ({
    searchQuery: messages.searchQuery,
    filterByStatus: messages.filterByStatus,
    filterByExperience: messages.filterByExperience,
  })
);

export const selectHasActiveFilters = createSelector(
  [selectMessagesState],
  (messages) =>
    messages.searchQuery !== "" ||
    messages.filterByStatus !== "all" ||
    messages.filterByExperience !== "all"
);

export const selectViewMode = createSelector(
  [selectMessagesState],
  (messages) => messages.viewMode
);

export const selectSorting = createSelector(
  [selectMessagesState],
  (messages) => ({
    sortBy: messages.sortBy,
    sortOrder: messages.sortOrder,
  })
);

export const selectMessagesPagination = createSelector(
  [selectMessagesState],
  (messages) => ({
    currentPage: messages.currentPage,
    itemsPerPage: messages.itemsPerPage,
  })
);

/**
 * Composite selector for all messages UI state
 */
export const selectCompleteMessagesUI = createSelector(
  [selectMessagesState],
  (messages) => ({
    selectedMessageId: messages.selectedMessageId,
    searchQuery: messages.searchQuery,
    filters: {
      status: messages.filterByStatus,
      experience: messages.filterByExperience,
    },
    viewMode: messages.viewMode,
    sorting: {
      sortBy: messages.sortBy,
      sortOrder: messages.sortOrder,
    },
    pagination: {
      currentPage: messages.currentPage,
      itemsPerPage: messages.itemsPerPage,
    },
  })
);

/**
 * Computed selectors for conditional UI rendering
 */

// Get list of active filter types (for showing filter chips)
export const selectActiveFilters = createSelector(
  [selectMessagesState],
  (messages) => {
    const active: string[] = [];
    if (messages.searchQuery) active.push("search");
    if (messages.filterByStatus !== "all") active.push("status");
    if (messages.filterByExperience !== "all") active.push("experience");
    return active;
  }
);

// Get filter count (for badge display)
export const selectActiveFilterCount = createSelector(
  [selectActiveFilters],
  (filters) => filters.length
);

export default messagesSlice.reducer;
